<div class="ai-dashboard">
  <main class="dashboard-main">

    <!-- Loading Session State -->
    @if (isLoadingSession) {
      <div class="processing-section centered-layout">
        <div class="processing-content-centered">
          <div class="jen-zen-wrapper">
            <div class="pulsing-circle"></div>
            <app-jen-zen-img [imgType]="JenZenImgType.HAPPY"></app-jen-zen-img>
          </div>
          <div class="text-content">
            <h3>Restoring Session...</h3>
            <p>Loading your previous work.</p>
          </div>
        </div>
      </div>
    }

    <!-- Empty state - large upload zone when no files -->
    @if (!isLoadingSession && !hasFiles()) {
      <div class="onboarding-empty-state">
      @if (hasBudgetingFeature()) {
        <div class="budgeting-mode-banner" [class.active]="isBudgetingMode()">
          <div class="banner-icon">
            <i class="material-symbols-rounded">account_balance_wallet</i>
          </div>
          <div class="banner-content">
            <h4>Budgeting Mode</h4>
            <p>Extract detailed line-item charges from each bill for budgeting — turn on before uploading.</p>
          </div>
          <mat-slide-toggle
            class="banner-toggle"
            [checked]="isBudgetingMode()"
            (change)="billService.toggleProMode()"
            aria-label="Toggle budgeting mode">
          </mat-slide-toggle>
        </div>
      }
      <app-upload-zone
        [hasFiles]="false"
        (filesSelected)="onFilesSelected($event)"
      ></app-upload-zone>
      </div>
    }

    <!-- Status & Stats Card -->
    @if (hasFiles()) {
      <div class="card-container-wrapper">
        <div class="card-container pf-tabs-main-wrapper status-card">
          <!-- JenZen Analysis Message -->
          @if (gameVisible()) {
            <!-- Game takeover. Persists across the crunch→done transition so the
                 game never yanks away: the bar flips to a clear "done" notice and the
                 player leaves on their own terms (Quit, or Start Import once ready). -->
            <div class="jen-zen-message-bar"
                 [class.budgeting-done]="!isProcessing() && successBillCount() > 0 && errorBills().length === 0">
              <div class="jen-zen-icon">
                @if (isProcessing() || (successBillCount() > 0 && errorBills().length === 0)) {
                  <app-jen-zen-img [imgType]="JenZenImgType.HAPPY"></app-jen-zen-img>
                } @else if (successBillCount() === 0) {
                  <app-jen-zen-img [imgType]="JenZenImgType.SAD"></app-jen-zen-img>
                } @else {
                  <app-jen-zen-img [imgType]="JenZenImgType.OK"></app-jen-zen-img>
                }
              </div>
              <div class="message-content">
                @if (isProcessing()) {
                  <p>Still crunching your numbers — budgeting can take up to a minute. Keep playing!</p>
                } @else if (successBillCount() === 0) {
                  <p>No bills were processed successfully. Check the errors below and retry or remove the failed uploads.</p>
                } @else if (errorBills().length > 0) {
                  <p><strong>All done!</strong> {{ successBillCount() }} bill{{ successBillCount() !== 1 ? 's are' : ' is' }} ready, but {{ errorBills().length }} failed to process.</p>
                } @else {
                  <p><strong>All done!</strong> Your bills are loaded and ready to import.</p>
                }
              </div>
              @if (!isProcessing() && successBillCount() > 0) {
                <button class="action-btn import ready" (click)="openBillAiFlow()">
                  <i class="material-symbols-rounded">cloud_upload</i>
                  <span>Start Import</span>
                  <span class="action-count">{{ successBillCount() }}</span>
                </button>
              }
              <button class="action-btn secondary" [matMenuTriggerFor]="gamesMenu">
                <i class="material-symbols-rounded">swap_horiz</i>
                <span>Switch game</span>
              </button>
              <button class="action-btn secondary" (click)="quitGame()">
                <i class="material-symbols-rounded">close</i>
                <span>{{ isProcessing() ? 'Leave Game' : 'Done' }}</span>
              </button>
            </div>
            <div class="game-panel">
              <ng-container *ngComponentOutlet="activeGame()"></ng-container>
              <!-- Drops onto the rink the moment crunching finishes, so a player
                   engrossed in the game can't miss that their data is ready. -->
              @if (!isProcessing() && successBillCount() > 0 && !doneNoticeAck()) {
                <div class="game-done-overlay">
                  <div class="game-done-card">
                    <i class="material-symbols-rounded">check_circle</i>
                    <h3>All done — your bills are ready!</h3>
                    <p>We finished crunching the numbers while you played.</p>
                    <div class="game-done-actions">
                      <button class="action-btn import ready" (click)="openBillAiFlow()">
                        <i class="material-symbols-rounded">cloud_upload</i>
                        <span>Start Import</span>
                        <span class="action-count">{{ successBillCount() }}</span>
                      </button>
                      <button class="action-btn secondary" (click)="keepPlaying()">
                        <i class="material-symbols-rounded">sports_esports</i>
                        <span>Keep Playing</span>
                      </button>
                    </div>
                  </div>
                </div>
              }
            </div>
          } @else if (!isProcessing()) {
            <div class="jen-zen-message-bar" [class.success]="successBillCount() > 0 && errorBills().length === 0">
              <div class="jen-zen-icon">
                @if (successBillCount() > 0 && errorBills().length === 0) {
                  <app-jen-zen-img [imgType]="JenZenImgType.HAPPY"></app-jen-zen-img>
                } @else if (successBillCount() === 0) {
                  <app-jen-zen-img [imgType]="JenZenImgType.SAD"></app-jen-zen-img>
                } @else {
                  <app-jen-zen-img [imgType]="JenZenImgType.OK"></app-jen-zen-img>
                }
              </div>
              <div class="message-content">
                @if (successBillCount() === 0) {
                  <p>No bills were processed successfully. Check the errors below and retry or remove the failed uploads.</p>
                } @else if (errorBills().length > 0) {
                  <p>{{ successBillCount() }} bill{{ successBillCount() !== 1 ? 's are' : ' is' }} ready, but {{ errorBills().length }} failed to process. You can import the successful ones or address the failures first.</p>
                } @else {
                  <p>Ready to import. Click 'Start Import' to proceed.</p>
                }
              </div>
              @if (successBillCount() > 0) {
                <button
                  class="action-btn import ready"
                  [disabled]="isProcessing()"
                  (click)="openBillAiFlow()">
                  <i class="material-symbols-rounded">cloud_upload</i>
                  <span>Start Import</span>
                  <span class="action-count">{{ successBillCount() }}</span>
                </button>
              }
            </div>
          } @else if (isBudgetingMode()) {
            <!-- Budgeting takes longer to crunch — fill the wait with a game.
                 Reverts to the standard "Ready to import" bar once bills load. -->
            <div class="jen-zen-message-bar budgeting-crunch">
              <div class="jen-zen-icon">
                <app-jen-zen-img [imgType]="JenZenImgType.HAPPY"></app-jen-zen-img>
              </div>
              <div class="message-content">
                <p>Budgeting mode can take up to a minute to finish. While I crunch these numbers, fancy a game?</p>
              </div>
              <button class="action-btn play" [matMenuTriggerFor]="gamesMenu">
                <i class="material-symbols-rounded">sports_esports</i>
                <span>Let's Play</span>
                <i class="material-symbols-rounded">arrow_drop_down</i>
              </button>
            </div>
          }

          <!-- Shared game picker — reachable from both the offer and the in-game "Switch game" button. -->
          <mat-menu #gamesMenu="matMenu">
            @for (game of games; track game.id) {
              <button mat-menu-item (click)="playGame(game)">{{ game.title }}</button>
            }
          </mat-menu>

          <div class="header-bar">
            <div class="header-stats">
              <div class="stat-badge electric">
                <i class="material-symbols-rounded status-indicator electric">bolt</i>
                <span class="stat-value">{{ stats().electric | number }}</span>
                <span class="stat-unit">kWh</span>
              </div>
              <div class="stat-badge gas">
                <i class="material-symbols-rounded status-indicator nat-gas">local_fire_department</i>
                <span class="stat-value">{{ stats().gas | number }}</span>
                <span class="stat-unit">{{ stats().gasUnit }}</span>
              </div>
            </div>
            @if (hasBudgetingFeature()) {
              <mat-slide-toggle
                class="budgeting-mode-toggle"
                [checked]="isBudgetingMode()"
                [disabled]="hasFiles()"
                (change)="billService.toggleProMode()"
                matTooltip="Budgeting Mode is locked while bills are loaded. Delete all bills or finish the import to change it."
                matTooltipClass="bill-warning-tooltip">
                Budgeting Mode
              </mat-slide-toggle>
            }
          </div>
        </div>
      </div>
    }

    <!-- Table Container with Tabs and Search -->
    @if (hasFiles()) {
      <div class="card-container-wrapper">
        <div class="card-container pf-tabs-main-wrapper table-container">
          <!-- Tab Header Row -->
          <div class="table-header">
            <div class="table-tabs">
              <button
                (click)="setViewMode('list')"
                [class.active]="viewMode() === 'list'"
                class="table-tab"
                >
                List View
              </button>
              <button
                (click)="setViewMode('hierarchy')"
                [class.active]="viewMode() === 'hierarchy'"
                class="table-tab"
                >
                Hierarchy
              </button>
            </div>
            <div class="table-header-actions">
              <!-- Undo Button -->
              @if (billService.canUndo()) {
                <button
                  (click)="billService.undo()"
                  class="undo-btn"
                  title="Undo last action"
                  >
                  <i class="material-symbols-rounded">undo</i>
                  Undo
                </button>
              }

              <!-- Add Bills Button -->
              <div class="upload-compact">
                <input
                  type="file"
                  multiple
                  accept="application/pdf"
                  (change)="handleFileInput($event)"
                  class="file-input-hidden"
                  #fileInput
                />
                <button class="btn-cta secondary" (click)="fileInput.click()">
                  <i class="material-symbols-rounded">add</i>
                  <span>Add more bills</span>
                </button>
              </div>
            </div>
          </div>
          <div class="table-bordered-area">
            <div class="table-search-row">
              <div class="search-container">
                <i class="material-symbols-rounded search-icon">search</i>
                <input
                  type="text"
                  placeholder="Search bills..."
                  [ngModel]="searchQuery()"
                  (ngModelChange)="billService.setSearchQuery($event)"
                  class="search-input"
                  />
                @if (searchQuery()) {
                  <button
                    (click)="billService.setSearchQuery('')"
                    class="search-clear"
                    aria-label="Clear search"
                    >
                    <i class="material-symbols-rounded">close</i>
                  </button>
                }
              </div>

              @if (showColumnSelector) {
                <div class="column-selector-backdrop" (click)="showColumnSelector = false"></div>
                <app-column-selector
                  [visibleColumns]="visibleColumns"
                  [isBudgetingMode]="isBudgetingMode()"
                  [hasBudgetingFeature]="hasBudgetingFeature()"
                  [positionStyle]="columnSelectorPosition"
                  (toggleColumn)="toggleColumn($event)"
                  (resetColumns)="resetColumns()"
                  (close)="showColumnSelector = false"
                ></app-column-selector>
              }
            </div>
            <!-- Hierarchy View -->
            @if (viewMode() === 'hierarchy') {
              <div class="view-container">
                @if (hierarchy().length === 0 && billService.getBills().length > 0 && errorBills().length === 0) {
                  <div class="processing-section centered-layout">
                    <div class="processing-content-centered compact">
                      <div class="jen-zen-wrapper">
                        <div class="pulsing-circle"></div>
                        <app-jen-zen-img [imgType]="JenZenImgType.HAPPY"></app-jen-zen-img>
                      </div>
                      <div class="text-content">
                        <h3>Processing Portfolio...</h3>
                        <p>Your bills are being analyzed and organized.</p>
                      </div>
                    </div>
                  </div>
                } @else {
                  @for (item of hierarchy(); track item) {
                    <app-hierarchy-item
                      [item]="item"
                      [visibleColumns]="visibleColumns"
                      [sortConfig]="sortConfig"
                      (openPairReview)="openPairReview($event)"
                    ></app-hierarchy-item>
                  }
                  <!-- Error bills shown as flat list below hierarchy -->
                  @if (errorBills().length > 0) {
                    <div class="error-bills-section">
                      <div class="error-bills-header">
                        <i class="material-symbols-rounded">error</i>
                        <span>{{ errorBills().length }} failed {{ errorBills().length === 1 ? 'bill' : 'bills' }}</span>
                      </div>
                      @for (bill of billService.errorBills(); track bill.id) {
                        <div class="error-bill-row">
                          <i class="material-symbols-rounded error-row-icon">description</i>
                          <span class="error-row-filename">{{ bill.file.name }}</span>
                          <span class="error-row-message">{{ bill.error || 'Processing error' }}</span>
                          @if (billRetriable()[bill.id]) {
                            <button (click)="billService.processBill(bill.id, bill.file)" class="error-retry-btn">
                              <i class="material-symbols-rounded">refresh</i>
                              Retry
                            </button>
                          }
                          <button (click)="billService.deleteBill(bill.id)" class="error-delete-btn">
                            <i class="material-symbols-rounded">delete</i>
                          </button>
                        </div>
                      }
                    </div>
                  }
                }
              </div>
            }
            <!-- List View -->
            @if (viewMode() === 'list') {
              <div class="view-container">
                <app-bill-list [visibleColumns]="visibleColumns" (openColumnSelector)="toggleColumnSelector($event)"></app-bill-list>
              </div>
            }
          </div>
        </div>
      </div>
    }

    <!-- Duplicate Resolver Modal -->
    @if (billService.resolvingDuplicate()) {
      <app-duplicate-resolver
        [newBill]="billService.resolvingDuplicate()!.newBill"
        [existingBill]="billService.resolvingDuplicate()!.existingBill"
        (resolve)="billService.resolveDuplicate($event)"
      ></app-duplicate-resolver>
    }

    <!-- Paired Bill Resolver Modal -->
    @if (reviewingPairedGroup()) {
      <app-paired-bill-resolver
        [group]="reviewingPairedGroup()!"
        (resolve)="billService.resolvePairedBill($event)"
      ></app-paired-bill-resolver>
    }

    <!-- Bill Comparison Modal -->
    @if (showComparison && comparisonBillsPair(); as pair) {
      <app-bill-comparison
        [bills]="pair"
        (close)="showComparison = false"
      ></app-bill-comparison>
    }

    <!-- Merge Modal -->
    <app-merge-modal
      [isOpen]="mergeModalState.isOpen"
      [type]="mergeModalState.type"
      [candidates]="mergeModalState.candidates"
      (closeModal)="closeMergeModal()"
      (confirm)="confirmMerge($event)"
    ></app-merge-modal>

    <!-- Selection Action Bar (floating buttons at bottom) -->
    <app-selection-action-bar
      [selectedBillCount]="selectedBillIds().length"
      [selectedEntityCount]="selectedEntityIds().length"
      [selectedAddressCount]="selectedAddressIds().length"
      [selectedMeterCount]="selectedMeterIds().length"
      [isHierarchyView]="viewMode() === 'hierarchy'"
      (compare)="showComparison = true"
      (deleteBills)="deleteBills()"
      (mergeEntities)="openMergeEntitiesModal()"
      (mergeAddresses)="openMergeAddressesModal()"
      (deleteEntities)="deleteEntities()"
      (deleteAddresses)="deleteAddresses()"
      (deleteMeters)="deleteMeters()"
    ></app-selection-action-bar>

    <!-- Import Review Modal -->
    @if (showImportReview()) {
      <div class="import-review-overlay">
        <div class="import-review-modal">
          <app-import-review
            [bills]="billService.getBills()"
            [context]="context!"
            (importComplete)="onImportComplete($event)"
            (importCancelled)="onImportCancelled()"
          ></app-import-review>
        </div>
      </div>
    }

    <!-- Bill AI Flow Modal -->
    @if (showBillAiFlow()) {
      <app-bill-ai-flow-shell
        [bills]="billService.getBills()"
        [context]="context!"
        (billAiFlowComplete)="onBillAiFlowComplete($event)"
        (billAiFlowCancelled)="onBillAiFlowCancelled()"
      />
    }
    <!-- Bill Preview Modal -->
    @if (billService.previewBill()) {
      <app-bill-preview
        [bill]="billService.previewBill()!"
        (close)="billService.previewBill.set(null)"
      ></app-bill-preview>
    }
  </main>
</div>
